import React, { useEffect, useRef, useState } from 'react';
import { Star, Plus, Trash2, Loader2, MessageSquare, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api/client';

/** Star rating input — click any star (1..5) to set rating. */
const StarInput = ({ value, onChange, size = 4 }) => (
  <div className="flex items-center gap-0.5" data-testid="star-input">
    {[1, 2, 3, 4, 5].map(n => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(n)}
        aria-label={`${n} star${n === 1 ? '' : 's'}`}
        data-testid={`star-input-${n}`}
        className="focus:outline-none"
      >
        <Star className={`w-${size} h-${size} ${n <= value ? 'text-[#F46A25] fill-[#F46A25]' : 'text-neutral-300'}`} />
      </button>
    ))}
  </div>
);

/**
 * Inline admin review manager: list existing reviews for a product and add new
 * ones. Recomputes rating aggregates server-side on save/delete.
 */
const ReviewManager = ({ productId, onRatingChanged }) => {
  const [open, setOpen] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ reviewer_name: '', rating: 5, title: '', text: '' });
  const loadedRef = useRef(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/products/${productId}/reviews`);
      setReviews(r.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (open && !loadedRef.current) {
      loadedRef.current = true;
      load();
    }
  }, [open]);

  const submit = async () => {
    if (!form.reviewer_name.trim() || !form.text.trim()) {
      toast.error('Reviewer name and review text are required');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/admin/products/${productId}/reviews`, {
        reviewer_name: form.reviewer_name.trim(),
        rating: Number(form.rating),
        title: form.title.trim() || null,
        text: form.text.trim(),
      });
      setForm({ reviewer_name: '', rating: 5, title: '', text: '' });
      await load();
      onRatingChanged?.();
      toast.success('Review added');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add review');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (rid) => {
    try {
      await api.delete(`/admin/reviews/${rid}`);
      setReviews(prev => prev.filter(r => r.id !== rid));
      onRatingChanged?.();
      toast.success('Review removed');
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="border-t border-neutral-200 pt-2 mt-2">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        data-testid={`review-manager-toggle-${productId}`}
        className="w-full flex items-center justify-between text-[11px] uppercase text-[#F46A25] font-bold hover:text-[#e05a15]"
      >
        <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Reviews {reviews.length > 0 && `(${reviews.length})`}</span>
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
      </button>
      {open && (
        <div className="mt-2 space-y-2" data-testid={`review-manager-${productId}`}>
          {/* Existing list */}
          {loading ? (
            <div className="flex justify-center py-2"><Loader2 className="w-4 h-4 animate-spin text-[#F46A25]" /></div>
          ) : reviews.length === 0 ? (
            <div className="text-[11px] text-neutral-500 italic">No reviews yet.</div>
          ) : (
            <div className="space-y-1.5 max-h-40 overflow-y-auto">
              {reviews.map(r => (
                <div key={r.id} className="bg-neutral-50 border border-neutral-200 rounded-sm p-2 text-xs">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <div className="flex">
                          {[1, 2, 3, 4, 5].map(n => (
                            <Star key={n} className={`w-3 h-3 ${n <= r.rating ? 'text-[#F46A25] fill-[#F46A25]' : 'text-neutral-300'}`} />
                          ))}
                        </div>
                        <span className="font-bold text-[#12263A]">{r.reviewer_name}</span>
                        <span className="text-neutral-400 text-[10px]">{new Date(r.created_at).toLocaleDateString('en-GB')}</span>
                      </div>
                      {r.title && <div className="font-semibold text-[#12263A] mt-0.5">{r.title}</div>}
                      <div className="text-neutral-700 whitespace-pre-line line-clamp-3">{r.text}</div>
                    </div>
                    <button
                      onClick={() => remove(r.id)}
                      aria-label="Delete review"
                      data-testid={`review-delete-${r.id}`}
                      className="text-red-600 hover:bg-red-50 p-1 rounded flex-shrink-0"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add form */}
          <div className="bg-white border border-neutral-200 rounded-sm p-2 space-y-1.5">
            <div className="text-[10px] uppercase font-bold text-neutral-500">Add a review</div>
            <div className="flex items-center gap-2">
              <input
                type="text"
                placeholder="Reviewer name"
                value={form.reviewer_name}
                onChange={e => setForm(f => ({ ...f, reviewer_name: e.target.value }))}
                data-testid="review-input-name"
                className="flex-1 border border-neutral-200 rounded-sm px-2 py-1 text-xs focus:outline-none focus:border-[#F46A25]"
              />
              <StarInput value={form.rating} onChange={v => setForm(f => ({ ...f, rating: v }))} size={4} />
            </div>
            <input
              type="text"
              placeholder="Review title (optional)"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              data-testid="review-input-title"
              className="w-full border border-neutral-200 rounded-sm px-2 py-1 text-xs focus:outline-none focus:border-[#F46A25]"
            />
            <textarea
              placeholder="Review text"
              value={form.text}
              onChange={e => setForm(f => ({ ...f, text: e.target.value }))}
              rows={2}
              data-testid="review-input-text"
              className="w-full border border-neutral-200 rounded-sm px-2 py-1 text-xs focus:outline-none focus:border-[#F46A25]"
            />
            <button
              type="button"
              onClick={submit}
              disabled={saving}
              data-testid="review-submit-btn"
              className="w-full bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold text-xs px-3 py-1.5 rounded-sm flex items-center justify-center gap-1 disabled:opacity-60"
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Add review
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewManager;
